import React, { FC, useState, useCallback, useRef } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import mergeInPlace from 'lodash/merge';
import setInPlace from 'lodash/set';

import { FormState } from 'ia-react-core/~/interfaces/FormState';
import { NestedFormInfo } from 'ia-react-core/~/interfaces/NestedFormInfo';
import { ControlInfo } from 'ia-react-core/~/interfaces/ControlInfo';

import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';
import merge from '../../../utils/accessors/merge';
import get from '../../../utils/accessors/get';
import { FormComposedProps } from './Form';

export type FormRegistrationInfos<Values> = {
  nestedFormInfos: NestedFormInfo<Values>[];
  nestedControlInfos: ControlInfo[];
};

export type FormRegistration<Values> = (infos: FormRegistrationInfos<Values>) => void;

const withFormMethods = <Values extends object, >(WrappedComponent: FC<FormComposedProps<Values>>) => {
  const WithFormMethods = (props: FormComposedProps<Values>) => {
    const { validate } = props;

    const registrationsRef = useRef<FormRegistration<Values>[]>([]);
    const [nestedFormInfos] = useState<NestedFormInfo<Values>[]>([]);
    const [nestedControlInfos] = useState<ControlInfo[]>([]);

    const watchRegistrations = useCallback((callback: FormRegistration<Values>) => {
      registrationsRef.current.push(callback);
      return () => {
        const index = registrationsRef.current.indexOf(callback);
        registrationsRef.current.splice(index, 1);
      };
    }, []);

    const [touchForm] = useState(() => (formState: Partial<FormState> = {}) => {
      nestedControlInfos.forEach(({ name }) => {
        setInPlace(formState, `touched.${name}`, true);
      });

      return formState;
    });

    const [validateForm] = useState(() => (values: Values, formState: Partial<FormState> = { errors: {} }) => {
      nestedFormInfos.forEach(({ name, validate: validateNestedForm }) => {
        setInPlace(formState, `forms.${name}.errors`, {});
        setInPlace(formState, `forms.${name}.isSubmitted`, true);

        const nestedFormState: Partial<FormState> = get(formState, `forms.${name}`);
        const nestedFormValues: Values = get(values, name);
        validateNestedForm(nestedFormValues, nestedFormState);
      });

      const { errors: formErrors, warnings: formWarnings } = validate(values);
      mergeInPlace(formState.errors, formErrors);
      mergeInPlace(formState.warnings, formWarnings);

      setInPlace(formState, 'isSubmitted', true);

      return formState;
    });

    const resetState = useCallback((state: { [index: string]: unknown}) => {
      Object.keys(state).forEach((fieldName) => {
        if (typeof state[fieldName] === 'object') {
          resetState(state[fieldName] as { [index: string]: unknown});
        } else {
          setInPlace(state, fieldName, false);
        }
      });
    }, []);

    const resetStateType = useCallback((formState: Partial<FormState>, type: 'dirty' | 'touched') => {
      const state = formState[type];

      if (typeof state === 'boolean') {
        setInPlace(formState, type, false);
      } else {
        resetState(state);
      }
    }, [resetState]);

    const resetFormState = useCallback((formState: Partial<FormState>) => {
      setInPlace(formState, 'isInitialized', false);
      setInPlace(formState, 'isSubmitted', false);
      resetStateType(formState, 'touched');
      resetStateType(formState, 'dirty');
    }, [resetStateType]);

    const [resetForm] = useState(() => (
      initialValues: Partial<Values>,
      currentFormState: Partial<FormState>,
      { resetValues = true, resetState: shouldResetState = true } = {},
    ) => {
      const baseValues = Array.isArray(initialValues) ? [] : {};
      const newValues = resetValues ? merge(baseValues, initialValues) as Values : undefined;

      const newFormState = {
        touched: currentFormState.touched,
        dirty: currentFormState.dirty,
        errors: validateForm(newValues).errors,
      };

      resetFormState(newFormState);

      nestedFormInfos.forEach(({ name, reset: resetNestedForm }) => {
        const nestedFormState: Partial<FormState> = get(currentFormState, `forms.${name}`);
        const { values: newNestedValues, formState } = resetNestedForm(nestedFormState, {
          resetValues,
          resetState: shouldResetState,
        });
        setInPlace(newValues, name, newNestedValues);
        setInPlace(newFormState, `forms.${name}`, formState);
      });

      return {
        values: newValues,
        formState: shouldResetState ? newFormState : undefined,
      };
    });

    const [setIsSubmitted] = useState(() => (isSubmitted: boolean, formState = { isSubmitted }) => {
      nestedFormInfos.forEach(({ name, setIsSubmitted: setIsSubmittedNestedForm }) => {
        setInPlace(formState, `forms.${name}.isSubmitted`, isSubmitted);

        const nestedFormState: Partial<FormState> = get(formState, `forms.${name}`);
        setIsSubmittedNestedForm(isSubmitted, nestedFormState);
      });

      return formState;
    });

    const [setIsSubmitting] = useState(() => (isSubmitting: boolean, formState = { isSubmitting }) => {
      nestedFormInfos.forEach(({ name, setIsSubmitting: setIsSubmittingNestedForm }) => {
        setInPlace(formState, `forms.${name}.isSubmitting`, isSubmitting);

        const nestedFormState: Partial<FormState> = get(formState, `forms.${name}`);
        setIsSubmittingNestedForm(isSubmitting, nestedFormState);
      });

      return formState;
    });

    const [setIsInitialized] = useState(() => (isInitialized: boolean, formState = { isInitialized }) => {
      nestedFormInfos.forEach(({ name, setIsInitialized: setIsInitializedNestedForm }) => {
        setInPlace(formState, `forms.${name}.isInitialized`, isInitialized);

        const nestedFormState: Partial<FormState> = get(formState, `forms.${name}`);
        setIsInitializedNestedForm(isInitialized, nestedFormState);
      });

      return formState;
    });

    return (
      <WrappedComponent
        nestedFormInfos={nestedFormInfos}
        nestedControlInfos={nestedControlInfos}
        registrations={registrationsRef.current}
        validateForm={validateForm}
        touchForm={touchForm}
        resetForm={resetForm}
        setIsSubmitting={setIsSubmitting}
        setIsInitialized={setIsInitialized}
        setIsSubmitted={setIsSubmitted}
        watchRegistrations={watchRegistrations}
        {...props}
      />
    );
  };

  WithFormMethods.displayName = getWrappedDisplayName('WithFormMethods', WrappedComponent);

  return hoistNonReactStatics(WithFormMethods, WrappedComponent);
};

export default withFormMethods;
